import { Link } from "react-router-dom";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount, useChainId, usePublicClient, useSwitchChain, useWriteContract } from "wagmi";
import { formatEther, parseEventLogs, type Address } from "viem";
import { ERC721DropAbi } from "@atelier/abis";
import type { ChainConfig } from "@atelier/config";
import { useState } from "react";
import { ExplorerLink } from "@/components/ExplorerLink";
import { ArtworkImage } from "@/components/ArtworkImage";
import { Spinner } from "@/components/Spinner";
import type { DropReads } from "@/hooks/useDropReads";
import type { AllowlistResponse } from "@/hooks/useAllowlistSign";

type Collection = {
  name: string;
  description?: string;
  imageCid?: string;
  bannerCid?: string;
  creatorAddress?: Address;
};

/**
 * The "above the fold" mint surface — banner, cover, name+meta, progress
 * bar, presale/reveal banners, and the qty + mint button (with both the
 * public mint and the allowlist mint paths).
 */
export function MintCard({
  chain,
  address,
  collection,
  reads,
  allowlist,
  onMinted,
}: {
  chain: ChainConfig;
  address: Address;
  collection: Collection | undefined;
  reads: DropReads;
  allowlist: { response: AllowlistResponse | undefined; eligible: boolean };
  onMinted: (newTokenIds: string[]) => void;
}) {
  const { address: wallet, isConnected } = useAccount();
  const walletChainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { writeContractAsync, isPending } = useWriteContract();
  const pc = usePublicClient({ chainId: chain.chainId });

  const [qty, setQty] = useState(1);
  const [status, setStatus] = useState<string | null>(null);
  const [lastTx, setLastTx] = useState<`0x${string}` | null>(null);
  const [mining, setMining] = useState(false);

  const {
    nameOn,
    maxSupply,
    totalMinted,
    priceWei,
    paused,
    mintStart,
    mintEnd,
    publicMintAt,
    onChainSigner,
    revealed,
    royaltyBps,
    refetch,
  } = reads;

  const now = Math.floor(Date.now() / 1000);
  const beforeStart = mintStart > now;
  const afterEnd = mintEnd !== 0 && mintEnd < now;
  const presaleConfigured =
    publicMintAt > 0 &&
    !!onChainSigner &&
    onChainSigner !== "0x0000000000000000000000000000000000000000";
  const inPresale = presaleConfigured && now < publicMintAt;
  const isOpenEdition = maxSupply === 0n;
  const progressPct = maxSupply > 0n ? Number((totalMinted * 10000n) / maxSupply) / 100 : 0;
  const supplyExhausted = !isOpenEdition && totalMinted >= maxSupply;

  const busy = isPending || mining;
  const presaleBlock = inPresale && !allowlist.eligible;
  const disabled =
    !isConnected || paused || beforeStart || afterEnd || supplyExhausted || presaleBlock;
  const effectivePriceWei =
    inPresale && allowlist.eligible && allowlist.response?.auth
      ? BigInt(allowlist.response.auth.priceWei)
      : priceWei;

  const ipfsGateway = chain.ipfsGateway ?? "https://dweb.link/ipfs/";

  const mint = async () => {
    if (!isConnected || !chain) return;
    if (walletChainId !== chain.chainId) {
      switchChain({ chainId: chain.chainId });
      return;
    }
    setMining(true);
    setStatus("Signing…");
    try {
      const useAllowlist =
        inPresale && allowlist.eligible && allowlist.response?.auth && allowlist.response.signature;
      const hash = useAllowlist
        ? await writeContractAsync({
            address,
            abi: ERC721DropAbi,
            functionName: "mintAllowlisted",
            args: [
              BigInt(qty),
              {
                to: allowlist.response!.auth!.to,
                maxQty: BigInt(allowlist.response!.auth!.maxQty),
                priceWei: BigInt(allowlist.response!.auth!.priceWei),
                deadline: BigInt(allowlist.response!.auth!.deadline),
              },
              allowlist.response!.signature!,
            ],
            value: BigInt(allowlist.response!.auth!.priceWei) * BigInt(qty),
            chainId: chain.chainId,
          })
        : await writeContractAsync({
            address,
            abi: ERC721DropAbi,
            functionName: "mint",
            args: [BigInt(qty)],
            value: priceWei * BigInt(qty),
            chainId: chain.chainId,
          });
      setLastTx(hash);
      setStatus("Confirming…");
      if (pc) {
        const receipt = await pc.waitForTransactionReceipt({ hash });
        const events = parseEventLogs({
          abi: ERC721DropAbi,
          eventName: "Transfer",
          logs: receipt.logs,
        });
        const newIds = events
          .filter((e) => wallet && e.args.to.toLowerCase() === wallet.toLowerCase())
          .map((e) => e.args.tokenId.toString());
        if (newIds.length > 0) onMinted(newIds);
      }
      setStatus(`Minted ${qty}. Enjoy.`);
      setQty(1);
      void refetch();
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    } finally {
      setMining(false);
    }
  };

  return (
    <>
      {collection?.bannerCid && (
        <div className="mb-6 overflow-hidden rounded-xl border border-border">
          <ArtworkImage
            alt=""
            src={`${ipfsGateway}${collection.bannerCid}`}
            className="h-48 w-full object-cover"
          />
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-[240px_1fr] md:items-start">
        {collection?.imageCid && (
          <ArtworkImage
            alt=""
            src={`${ipfsGateway}${collection.imageCid}`}
            className="aspect-square w-full rounded-xl border border-border object-cover"
          />
        )}
        <div>
          <h1 className="mb-1 text-2xl font-bold">{collection?.name ?? nameOn ?? "Collection"}</h1>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{chain.name}</span>
            <span>·</span>
            <ExplorerLink chain={chain} kind="address" value={address} />
            {collection?.creatorAddress && (
              <>
                <span>·</span>
                <span>
                  by{" "}
                  <Link
                    to={`/creator/${collection.creatorAddress}`}
                    className="hover:text-foreground"
                  >
                    {collection.creatorAddress.slice(0, 6)}…{collection.creatorAddress.slice(-4)}
                  </Link>
                </span>
              </>
            )}
          </div>
          {collection?.description && (
            <p className="mb-4 text-sm text-muted-foreground">{collection.description}</p>
          )}

          {!isOpenEdition && (
            <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary"
                style={{ width: `${Math.min(100, progressPct)}%` }}
              />
            </div>
          )}
          <div className="mb-4 text-xs text-muted-foreground">
            {isOpenEdition
              ? `${totalMinted.toString()} minted · open edition`
              : `${totalMinted.toString()} / ${maxSupply.toString()} minted`}
            {" · "}price {formatEther(priceWei)} {chain.nativeCurrency.symbol}
            {royaltyBps > 0 && <> · {(royaltyBps / 100).toFixed(2)}% royalty</>}
          </div>

          {paused && <div className="mb-3 warning p-2 text-xs">Paused by creator.</div>}
          {beforeStart && (
            <div className="mb-3 rounded-md border border-border bg-muted p-2 text-xs">
              Starts at {new Date(mintStart * 1000).toLocaleString()}
            </div>
          )}
          {afterEnd && (
            <div className="mb-3 rounded-md border border-border bg-muted p-2 text-xs">Ended.</div>
          )}
          {!revealed && (
            <div className="mb-3 rounded-md border border-violet-300 bg-violet-50 p-2 text-xs text-violet-900 dark:border-violet-700/40 dark:bg-violet-900/20 dark:text-violet-200">
              🎭 Pre-reveal — every minted token shows the same placeholder until the creator
              reveals the collection.
            </div>
          )}
          {inPresale && (
            <div className="mb-3 rounded-md border border-indigo-300 bg-indigo-50 p-2 text-xs text-indigo-900 dark:border-indigo-700/40 dark:bg-indigo-900/20 dark:text-indigo-200">
              Presale until {new Date(publicMintAt * 1000).toLocaleString()}.{" "}
              {wallet
                ? allowlist.eligible && allowlist.response?.auth
                  ? `Your wallet is on the allowlist (max ${allowlist.response.auth.maxQty}, ${formatEther(BigInt(allowlist.response.auth.priceWei))} ${chain.nativeCurrency.symbol} each).`
                  : "Your wallet is not on the allowlist — public mint opens later."
                : "Connect your wallet to check eligibility."}
            </div>
          )}

          {!isConnected ? (
            <ConnectButton />
          ) : walletChainId !== chain.chainId ? (
            <button className="btn-primary" onClick={() => switchChain({ chainId: chain.chainId })}>
              Switch to {chain.name}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="input h-10 w-24"
                min={1}
                value={qty}
                disabled={busy}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
              />
              <button
                className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                disabled={disabled || busy}
                onClick={() => void mint()}
              >
                {busy && <Spinner />}
                {isPending
                  ? "Confirm in wallet…"
                  : mining
                  ? "Confirming on-chain…"
                  : presaleBlock
                  ? "Allowlist only — wait for public mint"
                  : `${inPresale && allowlist.eligible ? "Allowlist mint" : "Mint"} ${qty} for ${formatEther(effectivePriceWei * BigInt(qty))} ${chain.nativeCurrency.symbol}`}
              </button>
            </div>
          )}

          {status && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{status}</span>
              {lastTx && (
                <ExplorerLink
                  chain={chain}
                  kind="tx"
                  value={lastTx}
                  label="view tx"
                  className="text-xs"
                />
              )}
            </div>
          )}

          {wallet && (
            <div className="mt-2 text-xs text-muted-foreground">
              Connected as{" "}
              <ExplorerLink chain={chain} kind="address" value={wallet} className="text-xs" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
